import { AnalysisErrorAlert as ErrorAlert } from './AnalysisErrorAlert.tsx';
import { Link } from 'react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
    Badge,
    Button,
    Card,
    Checkbox,
    Notice,
    TextArea,
    TextInput,
    EmptyState,
    Spinner,
    linkStyles,
} from '@lumlab/platform-ui';
import { learningReviewQueue } from '../contract/analysis-arbitration.ts';
import { analysisCommand } from './analysis-api.ts';

export function LearningRules({ decisionId }: { decisionId?: string }) {
    const { t } = useTranslation();
    const client = useQueryClient();
    const [page, setPage] = useState(1);
    const [reviewedDecisionId, setReviewedDecisionId] = useState(decisionId);
    const stale = reviewedDecisionId !== decisionId;
    const [ruleKey, setRuleKey] = useState('');
    const [version, setVersion] = useState('1');
    const [instruction, setInstruction] = useState('');
    const [conditions, setConditions] = useState('');
    const [tags, setTags] = useState('');
    const [conflictKey, setConflictKey] = useState('');
    const [pending, setPending] = useState<{ key: string; command: unknown } | null>(null);
    const save = useMutation({
        mutationFn: analysisCommand,
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: ['analysis'] });
        },
    });
    const query = useQuery({
        queryKey: ['analysis', 'rules', page],
        enabled: !decisionId,
        queryFn: async () =>
            learningReviewQueue.parse(await analysisCommand({ operation: 'list-learning-reviews', page })),
    });
    const submit = (input: unknown) => {
        const key = JSON.stringify(input);
        const command = pending?.key === key ? pending.command : { ...(input as object), id: crypto.randomUUID() };
        setPending({ key, command });
        save.mutate(command);
    };
    if (decisionId)
        return (
            <details>
                <summary>{t('tenders.arbitration.proposeRule')}</summary>
                <form
                    className="space-y-3 pt-3"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (stale) return;
                        submit({
                            operation: 'propose-rule',
                            ruleKey,
                            version: Number(version),
                            decisionId: reviewedDecisionId,
                            instruction,
                            conditions,
                            scope: {
                                vertical: 'pv',
                                task: 'dce-to-json',
                                tags: tags
                                    .split(',')
                                    .map((tag) => tag.trim())
                                    .filter(Boolean),
                            },
                            conflictKey: conflictKey || null,
                        });
                    }}
                >
                    <Notice>{t('tenders.arbitration.rulePolicy')}</Notice>
                    <p className="break-all text-sm">
                        {t('tenders.arbitration.decisionOrigin', { id: reviewedDecisionId })}
                    </p>
                    {stale && (
                        <Notice tone="warning">
                            {t('tenders.arbitration.draftStale')}
                            <Button
                                variant="default"
                                onClick={() => {
                                    setReviewedDecisionId(decisionId);
                                    setPending(null);
                                    save.reset();
                                }}
                            >
                                {t('tenders.arbitration.reviewLatest')}
                            </Button>
                        </Notice>
                    )}
                    <TextInput
                        label={t('tenders.arbitration.ruleKey')}
                        value={ruleKey}
                        onChange={(event) => setRuleKey(event.target.value)}
                        pattern="[a-z0-9](?:[a-z0-9]|-){0,99}"
                        required
                    />
                    <TextInput
                        label={t('tenders.arbitration.version')}
                        type="number"
                        min={1}
                        max={100000}
                        value={version}
                        onChange={(event) => setVersion(event.target.value)}
                        required
                    />
                    <TextArea
                        label={t('tenders.arbitration.instruction')}
                        value={instruction}
                        onChange={(event) => setInstruction(event.target.value)}
                        required
                        maxLength={20000}
                    />
                    <TextArea
                        label={t('tenders.arbitration.conditions')}
                        value={conditions}
                        onChange={(event) => setConditions(event.target.value)}
                        required
                        maxLength={20000}
                    />
                    <TextInput
                        label={t('tenders.arbitration.tags')}
                        value={tags}
                        onChange={(event) => setTags(event.target.value)}
                    />
                    <TextInput
                        label={t('tenders.arbitration.conflictKey')}
                        value={conflictKey}
                        onChange={(event) => setConflictKey(event.target.value)}
                        pattern="[a-z0-9](?:[a-z0-9]|-){0,99}"
                    />
                    {save.error && <ErrorAlert error={save.error} />}{' '}
                    {save.isSuccess && <Notice tone="success">{t('tenders.arbitration.ruleProposed')}</Notice>}
                    {save.error && pending && (
                        <Button variant="default" loading={save.isPending} onClick={() => save.mutate(pending.command)}>
                            {t('tenders.arbitration.retryExact')}
                        </Button>
                    )}
                    <Button type="submit" variant="default" disabled={stale} loading={save.isPending}>
                        {t('tenders.arbitration.proposeRule')}
                    </Button>
                </form>
            </details>
        );
    return (
        <section className="space-y-4">
            <h2 className="text-xl font-semibold">{t('tenders.arbitration.rules')}</h2>
            <Notice>{t('tenders.arbitration.rulePolicy')}</Notice>
            {query.isPending && <Spinner label={t('tenders.arbitration.loading')} />}
            {query.data?.items.length === 0 && <EmptyState title={t('tenders.arbitration.noRules')} />}
            {query.error && <ErrorAlert error={query.error} />}
            {save.error && <ErrorAlert error={save.error} />}
            {query.data?.items.map((item) => (
                <RuleReview key={item.revision.id} item={item} submit={submit} pending={save.isPending} />
            ))}
            <div className="flex gap-3">
                <Button variant="default" disabled={page === 1} onClick={() => setPage(page - 1)}>
                    {t('tenders.arbitration.previous')}
                </Button>
                <Button variant="default" disabled={!query.data?.hasMore} onClick={() => setPage(page + 1)}>
                    {t('tenders.arbitration.next')}
                </Button>
            </div>
        </section>
    );
}
function RuleReview({
    item,
    submit,
    pending,
}: {
    item: z.infer<typeof learningReviewQueue>['items'][number];
    submit: (input: unknown) => void;
    pending: boolean;
}) {
    const { t } = useTranslation();
    const [confirmed, setConfirmed] = useState(false);
    const [reason, setReason] = useState('');
    const decision = z.object({ resolution: z.string(), justification: z.string() }).parse(item.decision.payload);
    const rule = z
        .object({
            ruleKey: z.string(),
            version: z.number(),
            instruction: z.string(),
            conditions: z.string().optional(),
            scope: z.object({
                vertical: z.string().nullable(),
                task: z.string().nullable(),
                tags: z.array(z.string()),
            }),
            conflictKey: z.string().nullable(),
            decisionId: z.string(),
        })
        .parse(item.revision.payload);
    return (
        <Card className="space-y-3">
            <h3>
                {rule.ruleKey} · {rule.version}
            </h3>
            <Badge>{t(`tenders.arbitration.ruleState.${item.state}`)}</Badge>
            <p>{rule.instruction}</p>
            <p>{rule.conditions ?? t('tenders.arbitration.conditionsUnknown')}</p>
            <p>
                {rule.scope.vertical} · {rule.scope.task} · {rule.scope.tags.join(', ')}
            </p>
            <p>{rule.conflictKey}</p>
            <p className="break-all text-sm">{t('tenders.arbitration.ruleRevision', { id: item.revision.id })}</p>
            <p className="break-all text-sm">{t('tenders.arbitration.decisionOrigin', { id: rule.decisionId })}</p>
            <p>{decision.resolution}</p>
            <p>{decision.justification}</p>
            <Link className={linkStyles} to={`/admin/products/tenders/analysis/${item.runId}`}>
                {t('tenders.arbitration.ruleOrigin')}
            </Link>
            {item.state === 'proposed' && (
                <>
                    <Checkbox
                        label={t('tenders.arbitration.confirmRule')}
                        checked={confirmed}
                        onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    <Button
                        variant="default"
                        disabled={!confirmed}
                        loading={pending}
                        onClick={() =>
                            submit({
                                operation: 'approve-rule',
                                revisionId: item.revision.id,
                                confirmHumanApproval: true,
                            })
                        }
                    >
                        {t('tenders.arbitration.approveRule')}
                    </Button>
                </>
            )}
            {item.state === 'active' && (
                <>
                    <TextInput
                        label={t('tenders.arbitration.retirementReason')}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        maxLength={2000}
                    />
                    <Button
                        variant="default"
                        disabled={!reason.trim()}
                        loading={pending}
                        onClick={() => submit({ operation: 'retire-rule', revisionId: item.revision.id, reason })}
                    >
                        {t('tenders.arbitration.retireRule')}
                    </Button>
                </>
            )}
        </Card>
    );
}
